import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { isOverdue, isClosedStatus } from '../lib/actionItemStyles'

interface ActionItem {
  id: string
  status: string
  dueDate?: string | null
}

interface Props {
  items: ActionItem[]
  className?: string
}

const ActionItemsStats: React.FC<Props> = ({ items, className }) => {
  const { t } = useTranslation()

  const stats = useMemo(() => {
    const open = items.filter((i) => i.status === 'OPEN').length
    const inProgress = items.filter((i) => i.status === 'IN_PROGRESS').length
    const overdue = items.filter(isOverdue).length
    const completed = items.filter((i) => isClosedStatus(i.status)).length
    return { open, inProgress, overdue, completed }
  }, [items])

  const cards = [
    {
      key: 'open',
      label: t('ActionItem_stats_open'),
      value: stats.open,
      icon: AlertCircle,
      classes: 'border-amber-200 bg-amber-50 text-amber-700',
    },
    {
      key: 'in_progress',
      label: t('ActionItem_stats_in_progress'),
      value: stats.inProgress,
      icon: Clock,
      classes: 'border-blue-200 bg-blue-50 text-blue-700',
    },
    {
      key: 'overdue',
      label: t('ActionItem_stats_overdue'),
      value: stats.overdue,
      icon: AlertTriangle,
      classes: 'border-red-200 bg-red-50 text-red-700',
    },
    {
      key: 'completed',
      label: t('ActionItem_stats_completed'),
      value: stats.completed,
      icon: CheckCircle2,
      classes: 'border-green-200 bg-green-50 text-green-700',
    },
  ]

  return (
    <div className={cn('grid grid-cols-2 md:grid-cols-4 gap-3', className)}>
      {cards.map((c) => {
        const Icon = c.icon
        return (
          <div
            key={c.key}
            className={cn(
              'rounded-lg border p-3 flex items-center gap-3',
              c.classes
            )}
          >
            <Icon className="h-5 w-5 shrink-0" />
            <div className="min-w-0">
              <div className="text-2xl font-bold leading-none">{c.value}</div>
              <div className="text-xs mt-1 opacity-80 truncate">{c.label}</div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ActionItemsStats
